/**
 * Reducer send attempts.
 *
 * Правила: correlation только по exact `clientMessageId`; ACK без ID не
 * выбирает attempt по позиции в очереди; первая терминальная интерпретация
 * побеждает, кроме provisional timeout, который поздний ACK перезаписывает.
 */
import { SendAttempt, SendAttemptState } from "./types";

/** Decoded `CHAT_SEND_RESULT` в минимальной форме, без `error_message`. */
export interface AckInput {
  success: boolean;
  clientMessageId: string;
  errorCode: string;
  senderMessageId: string;
}

/**
 * Коды, для которых wire доказывает отсутствие записи.
 * `CHAT_STORAGE_UNAVAILABLE` и `INTERNAL_ERROR` сюда не входят: их shape не
 * различает failure до и после вызова storage, поэтому консервативный
 * результат — `durability-unknown`.
 */
const DEFINITELY_UNSENT = new Set([
  "EMPTY_MESSAGE",
  "CONTROL_CHAR",
  "MESSAGE_TOO_LONG",
  "MESSAGE_TOO_LARGE",
  "INVALID_CLIENT_MESSAGE_ID",
  "RATE_LIMIT",
  "CHAT_DISABLED",
]);

/** Терминальные состояния из ACK. Provisional timeout к ним не относится. */
function isTerminal(attempt: SendAttempt): boolean {
  if (attempt.state === "acknowledged" || attempt.state === "rejected") {
    return true;
  }
  return attempt.unknownSource === "terminal-ack";
}

function nextState(ack: AckInput): SendAttemptState {
  if (ack.success) return "acknowledged";
  return DEFINITELY_UNSENT.has(ack.errorCode)
    ? "rejected"
    : "durability-unknown";
}

export function createAttempt(
  attempts: SendAttempt[],
  clientMessageId: string,
  text: string
): SendAttempt[] {
  return [...attempts, { clientMessageId, text, state: "pending" }];
}

export function applyAck(
  attempts: SendAttempt[],
  ack: AckInput
): SendAttempt[] {
  if (!ack.clientMessageId) return attempts;

  const target = attempts.find(
    (attempt) => attempt.clientMessageId === ack.clientMessageId
  );
  // Неизвестный ID: attempt уже очищена новой generation.
  if (!target || isTerminal(target)) return attempts;

  const state = nextState(ack);
  return attempts.map((attempt) =>
    attempt.clientMessageId === ack.clientMessageId
      ? {
          ...attempt,
          state,
          errorCode: ack.errorCode || undefined,
          senderMessageId: ack.senderMessageId || undefined,
          unknownSource:
            state === "durability-unknown" ? "terminal-ack" : undefined,
        }
      : attempt
  );
}

/** UI-таймер переводит pending в provisional unknown. Ничего не отправляет. */
export function markTimedOut(
  attempts: SendAttempt[],
  clientMessageId: string
): SendAttempt[] {
  return attempts.map((attempt) =>
    attempt.clientMessageId === clientMessageId && attempt.state === "pending"
      ? { ...attempt, state: "durability-unknown", unknownSource: "timeout" }
      : attempt
  );
}

export function removeAttempt(
  attempts: SendAttempt[],
  clientMessageId: string
): SendAttempt[] {
  return attempts.filter(
    (attempt) => attempt.clientMessageId !== clientMessageId
  );
}
